<script setup>
import { ref } from 'vue'
import { exportReport } from '@/services/reportApi'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from '@/components/ui/toast'
import { Download } from 'lucide-vue-next'

const props = defineProps({
  report: {
    type: Object,
    required: true
  },
  organizationId: {
    type: [String, Number],
    required: true
  }
})

const isOpen = ref(false)
const isExporting = ref(false)
const exportFormat = ref('pdf')

const FORMAT_OPTIONS = [
  { value: 'pdf', label: 'PDF Document (.pdf)' },
  { value: 'docx', label: 'Word Document (.docx)' },
  { value: 'markdown', label: 'Markdown (.md)' },
]

const FORMAT_MIME_TYPES = {
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  markdown: 'text/markdown',
}

const FORMAT_EXTENSIONS = {
  pdf: 'pdf',
  docx: 'docx',
  markdown: 'md',
}

const open = () => {
  exportFormat.value = 'pdf'
  isOpen.value = true
}

const close = () => {
  isOpen.value = false
}

const handleExport = async () => {
  isExporting.value = true
  try {
    const version = props.report.version || null
    const response = await exportReport(
      props.organizationId,
      props.report.report_uuid,
      exportFormat.value,
      version
    )

    const ext = FORMAT_EXTENSIONS[exportFormat.value]
    const mimeType = FORMAT_MIME_TYPES[exportFormat.value]

    // Get filename from Content-Disposition header or create default
    let filename = `${props.report.title || 'report'}.${ext}`
    if (response.headers && response.headers['content-disposition']) {
      const contentDisposition = response.headers['content-disposition']
      const filenameStarMatch = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;\s]+)/i)
      if (filenameStarMatch) {
        try {
          filename = decodeURIComponent(filenameStarMatch[1])
        } catch {
          // fall through to legacy filename=
        }
      }
      if (!filenameStarMatch) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
        if (filenameMatch) {
          filename = filenameMatch[1]
        }
      }
    }

    // Create blob and trigger download
    const blob = new Blob([response.data], { type: mimeType })
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(downloadUrl)

    toast({
      title: 'Export successful',
      description: `${filename} downloaded successfully`,
      variant: 'default'
    })

    close()

  } catch (error) {
    console.error('Error exporting report:', error)
    const errorMessage =
      error instanceof Error && error.message
        ? error.message
        : 'Failed to export report. Please try again.'
    toast({
      title: 'Export failed',
      description: errorMessage,
      variant: 'destructive'
    })
  } finally {
    isExporting.value = false
  }
}

defineExpose({ open })
</script>

<template>
  <Dialog v-model:open="isOpen">
    <DialogContent class="max-w-[400px]">
      <DialogHeader>
        <DialogTitle>Export Report</DialogTitle>
        <DialogDescription>
        </DialogDescription>
      </DialogHeader>

      <div class="space-y-4 py-4">
        <div class="space-y-2">
          <Label>Format</Label>
          <Select v-model="exportFormat">
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem
                v-for="option in FORMAT_OPTIONS"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <DialogFooter>
        <Button variant="ghost" @click="close" :disabled="isExporting">
          Cancel
        </Button>
        <Button @click="handleExport" :disabled="isExporting">
          <Download class="h-4 w-4 mr-2" />
          {{ isExporting ? 'Exporting...' : 'Export' }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
